import type { Name } from '@/services/getAlgoliaExperts';
import type { UserV2 } from '@/services/getUserV2';

type GetProfileNameAndInitialsParams = {
  firstName?: string | UserV2['firstName'] | Name['firstName'];
  lastName?: string | UserV2['lastName'] | Name['lastName'];
  identifiesAsDoctor?: boolean | UserV2['identifiesAsDoctor'];
  [key: string]: unknown;
};

const normalizeNamePart = (value: unknown): string => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return String(value).trim();
};

export const getProfileNameAndInitials = ({
  firstName = '',
  lastName = '',
  identifiesAsDoctor = false,
}: GetProfileNameAndInitialsParams) => {
  const [normalizedFirstName, normalizedLastName] = [
    normalizeNamePart(firstName),
    normalizeNamePart(lastName),
  ];

  const fullName = [normalizedFirstName, normalizedLastName].filter(Boolean).join(' ');

  const name = identifiesAsDoctor && fullName ? `Dr. ${fullName}` : fullName;

  const formalName =
    identifiesAsDoctor && normalizedLastName ? `Dr. ${normalizedLastName}` : fullName;

  const initials = `${normalizedFirstName.charAt(0)}${normalizedLastName.charAt(0)}`.toUpperCase();

  const nameWithoutDoctor = name.replace(/^Dr\.?\s+/i, '');

  return { name, formalName, initials, nameWithoutDoctor };
};

export const getProfileNameOrFallback = (
  profile: GetProfileNameAndInitialsParams | null | undefined,
  fallbackName: string
) => getProfileNameAndInitials(profile ?? {}).name || fallbackName;

/** Salutation-style label: `Dr. {lastName}` when `identifiesAsDoctor` and last name exist; otherwise full name without the long-form Dr. prefix. */
export const getProfileFormalNameOrFallback = (
  profile: GetProfileNameAndInitialsParams | null | undefined,
  fallbackName: string
) => {
  const { formalName, name } = getProfileNameAndInitials(profile ?? {});

  return formalName || name || fallbackName;
};
